import { format as formatDate, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatoPorcentaje } from './formato-precio';

/**
 * Global decimal places setting — updated from tenant config on login.
 * Default: 2. Can be changed via admin settings.
 */
let _globalDecimalPlaces = 2;

export function setGlobalDecimalPlaces(dp: number) {
  _globalDecimalPlaces = dp;
}

export function getGlobalDecimalPlaces(): number {
  return _globalDecimalPlaces;
}

/**
 * Locale de los montos de toda la app: es-BO («USD 2.308.579,50», «Bs 1.234,50»),
 * como la maqueta y el resto de Prixia (`formato-precio.ts`, `moneda.ts`). Antes era
 * es-MX («USD 2,308,579.50»): Cotiza escribía distinto que Compite y Comercia en la
 * misma pantalla de Inicio. El Pricer Datec mantiene a propósito el formato de su
 * documento (`formatoPrecioEn`).
 */
export const LOCALE_DE_MONTOS = 'es-BO';

/**
 * Formatea un número como moneda. Si el código no es ISO 4217 válido
 * (e.g. USDC, criptomonedas, códigos locales como BS), cae en un formato
 * numérico con el código como sufijo para evitar RangeError de Intl.
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale: string = LOCALE_DE_MONTOS
): string {
  const code = (currency ?? 'USD').toString().trim().toUpperCase() || 'USD';
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: _globalDecimalPlaces,
      maximumFractionDigits: _globalDecimalPlaces,
    }).format(safeAmount);
  } catch {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: _globalDecimalPlaces,
      maximumFractionDigits: _globalDecimalPlaces,
    }).format(safeAmount);
    return `${formatted} ${code}`;
  }
}

/**
 * Formatea un número como porcentaje. Un solo formateador para todo el front
 * (R7-3): delega en `formatoPorcentaje` (es-BO, signo menos real, espacio antes
 * del «%»: «−20,7 %»), no arma el texto a mano con `toFixed(…)%`.
 */
export function formatPercentage(value: number, decimals?: number): string {
  return formatoPorcentaje(value, { decimales: decimals ?? _globalDecimalPlaces });
}

/**
 * Formatea un número con separadores de miles
 */
export function formatNumber(
  value: number,
  decimals?: number,
  locale: string = LOCALE_DE_MONTOS
): string {
  const dp = decimals ?? _globalDecimalPlaces;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).format(value);
}

/**
 * Marcador que se muestra cuando no hay fecha, o cuando lo que llegó no es una.
 * Un guion dice "no sé" y se lee como tal; un `0` o un `Invalid Date` mienten.
 */
export const SIN_FECHA = '—';

/**
 * Convierte a `Date` lo que venga, y devuelve `null` si no hay fecha válida.
 *
 * 🔴 `formatDate` de date-fns **tira `RangeError: Invalid time value`** con una
 * fecha inválida, y como la aplicación no tiene error boundary, ese throw en una
 * tarjetita se lleva puesta la pantalla entera: `#root` queda vacío. Pasó en
 * producción con "Mi Perfil" el 2026-09-09 — el `createdAt` no venía en la
 * respuesta del login y `parseISO("undefined")` alcanzó para tirar la app.
 *
 * Por eso los formateadores de acá abajo nunca tiran: mismo criterio que
 * `formatCurrency` con los códigos de moneda que no son ISO 4217.
 */
function aFechaValida(date: Date | string | null | undefined): Date | null {
  if (date === null || date === undefined || date === '') return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return null;
  return dateObj;
}

/**
 * Formatea una fecha. Sin fecha válida devuelve `SIN_FECHA`, no tira.
 */
export function formatDateString(
  date: Date | string | null | undefined,
  formatStr: string = 'dd/MM/yyyy'
): string {
  const dateObj = aFechaValida(date);
  if (!dateObj) return SIN_FECHA;
  return formatDate(dateObj, formatStr, { locale: es });
}

/**
 * Formatea una fecha con hora. Sin fecha válida devuelve `SIN_FECHA`, no tira.
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  const dateObj = aFechaValida(date);
  if (!dateObj) return SIN_FECHA;
  return formatDate(dateObj, "dd/MM/yyyy 'a las' HH:mm", { locale: es });
}

/**
 * Formatea una fecha relativa (hace 2 horas, hace 3 días, etc.).
 * Sin fecha válida devuelve `SIN_FECHA`, no tira.
 */
export function formatRelativeTime(date: Date | string | null | undefined): string {
  const dateObj = aFechaValida(date);
  if (!dateObj) return SIN_FECHA;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins === 1) return 'Hace 1 minuto';
  if (diffMins < 60) return `Hace ${diffMins} minutos`;
  if (diffHours === 1) return 'Hace 1 hora';
  if (diffHours < 24) return `Hace ${diffHours} horas`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;

  return formatDateString(dateObj);
}

/**
 * Trunca un texto a una longitud máxima
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Convierte a mayúsculas la primera letra
 */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
